/**
* @copyright Copyright (C) 2021 Nile AI, Inc - All Rights Reserved
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
*/

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import KnArrowDownIcon from 'components/icons/ArrowDownIcon';
import { METRICS_TYPE } from 'Constants';
import { countMetricsByBuckets, countTopSymptomsByType } from 'utils/metrics';
import KnMetricsSummary from './MetricsSummary';
import KnMetricsDetails from './MetricsDetails';
import { KnMetricsTitle } from './styles';

/**
 * Displays the collapsible card for seizures or symptoms patient metrics.
 */
const KnMetricsCard = (props) => {
  const { type, data, timeRangeEnd } = props;
  const { t: translate } = useTranslation();
  const isSeizureMetrics = (type === METRICS_TYPE.seizure);
  const panelTitle = isSeizureMetrics
    ? translate('PATIENT_RECORD.seizureMetrics.title')
    : translate('PATIENT_RECORD.symptomMetrics.title');

  /**
   * For seizures, we can aggregate data into buckets right away,
   * for symptoms, we will do the aggregation inside the countTopSymptomsByType
   *  as we need to group them first by type, and show only the top ones
   */
  const aggregatedData = useMemo(() => (
    isSeizureMetrics
      ? countMetricsByBuckets(data, timeRangeEnd) : countTopSymptomsByType(data, timeRangeEnd)
  ), [data, timeRangeEnd, isSeizureMetrics]);

  return (
    <ExpansionPanel square elevation={0}>
      <ExpansionPanelSummary expandIcon={aggregatedData.length > 0 && <KnArrowDownIcon data-testid="panel-expand-icon" />}>
        <Typography display="block" component={KnMetricsTitle}>{panelTitle}</Typography>
        <KnMetricsSummary
          rawData={data}
          type={type}
          timeRangeEnd={timeRangeEnd}
        />
      </ExpansionPanelSummary>

      {aggregatedData.length > 0
        && (
          <ExpansionPanelDetails>
            <KnMetricsDetails type={type} aggregatedData={aggregatedData} />
          </ExpansionPanelDetails>
        )}
    </ExpansionPanel>
  );
};

KnMetricsCard.propTypes = {
  type: PropTypes.oneOf([METRICS_TYPE.seizure, METRICS_TYPE.symptom]).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  timeRangeEnd: PropTypes.instanceOf(Date).isRequired,
};

export default KnMetricsCard;
